import { useMemo } from 'react';
import { useGetSummaryMessagesByWeek } from '@sendible/frontendv2/src/models/calendar';
import { Container } from './index.styles';
import { WeekCard } from '../WeekCard';
import { useCalendarContext } from '../../context';
import { getWeekRange, keyFromDate, shiftWeek } from '../../utils';

export const WeekSlider = () => {
  const { currentDate, nextWeek, prevWeek, userWeekStartDay } = useCalendarContext();

  const prevWeekDate = useMemo(() => shiftWeek(currentDate, -1), [currentDate]);
  const nextWeekDate = useMemo(() => shiftWeek(currentDate, 1), [currentDate]);

  const { data: previousWeekData } = useGetSummaryMessagesByWeek(prevWeekDate, userWeekStartDay);
  const { data: currentWeekData } = useGetSummaryMessagesByWeek(currentDate, userWeekStartDay);
  const { data: nextWeekData } = useGetSummaryMessagesByWeek(nextWeekDate, userWeekStartDay);

  const buildPostsPerDays = (
    weekData: CalendarData[] | undefined,
    weekRefDate: CalendarMonthString
  ): [number, number, number, number, number, number, number] => {
    const [weekStartISO] = getWeekRange(weekRefDate, userWeekStartDay);
    const [y, m, d] = weekStartISO.split('-').map(Number);
    const startDate = new Date(y, m - 1, d);
    const counts: number[] = Array(7).fill(0);

    if (weekData) {
      const map = new Map<string, number>();

      weekData.forEach((day) => map.set(day.date, day.messageCount));
      for (let i = 0; i < 7; i++) {
        const dt = new Date(startDate);

        dt.setDate(startDate.getDate() + i);

        const iso = keyFromDate(dt);

        counts[i] = map.get(iso) || 0;
      }
    }

    return counts as [number, number, number, number, number, number, number];
  };

  const [prevWeekStart, prevWeekEnd] = useMemo(() => getWeekRange(prevWeekDate, userWeekStartDay), [prevWeekDate, userWeekStartDay]);
  const [currWeekStart, currWeekEnd] = useMemo(() => getWeekRange(currentDate, userWeekStartDay), [currentDate, userWeekStartDay]);
  const [nextWeekStart, nextWeekEnd] = useMemo(() => getWeekRange(nextWeekDate, userWeekStartDay), [nextWeekDate, userWeekStartDay]);
  const prevPosts = useMemo(() => buildPostsPerDays(previousWeekData, prevWeekDate), [previousWeekData, prevWeekDate]);
  const currPosts = useMemo(() => buildPostsPerDays(currentWeekData, currentDate), [currentWeekData, currentDate]);
  const nextPosts = useMemo(() => buildPostsPerDays(nextWeekData, nextWeekDate), [nextWeekData, nextWeekDate]);

  return (
    <Container
      data-export-hide
      data-testid="week-cards-menu"
    >
      <WeekCard
        data-testid="prev-week-card"
        onClick={prevWeek}
        postsPerDays={prevPosts}
        weekStart={prevWeekStart}
        weekEnd={prevWeekEnd}
      />

      <WeekCard
        data-testid="current-week-card"
        postsPerDays={currPosts}
        weekStart={currWeekStart}
        weekEnd={currWeekEnd}
        isHighlighted
      />

      <WeekCard
        data-testid="next-week-card"
        onClick={nextWeek}
        postsPerDays={nextPosts}
        weekStart={nextWeekStart}
        weekEnd={nextWeekEnd}
      />
    </Container>
  );
};
